import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  MapPin, 
  Clock, 
  User, 
  Eye, 
  ThumbsUp, 
  Filter,
  Search,
  CheckCircle,
  XCircle,
  Send,
  Loader2,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Video,
  ExternalLink,
  Trash2,
  Droplets,
  TreePine,
  Construction,
  Flame,
  HelpCircle,
  X
} from 'lucide-react';

interface IssueReport {
  id: string;
  user_id: string;
  category: string;
  description: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  media_urls: string[];
  is_anonymous: boolean;
  status: 'submitted' | 'verified' | 'sent_to_authority' | 'resolved' | 'rejected';
  created_at: string;
  updated_at: string;
  points_awarded: number;
  upvotes: number;
  profiles?: {
    username: string;
    avatar_url: string;
  };
  ai_confidence?: number;
  ai_tags?: string[];
}

const ISSUE_CATEGORIES = [
  { 
    id: 'waste_dump', 
    label: 'Waste Dump', 
    icon: Trash2, 
    color: 'text-orange-600',
    bg: 'bg-orange-100'
  },
  { 
    id: 'illegal_tree_cutting', 
    label: 'Illegal Tree Cutting', 
    icon: TreePine, 
    color: 'text-green-600',
    bg: 'bg-green-100'
  },
  { 
    id: 'water_contamination', 
    label: 'Water Contamination', 
    icon: Droplets, 
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  },
  { 
    id: 'construction_pollution', 
    label: 'Construction Pollution', 
    icon: Construction, 
    color: 'text-yellow-600',
    bg: 'bg-yellow-100'
  },
  { 
    id: 'open_burning', 
    label: 'Open Burning', 
    icon: Flame, 
    color: 'text-red-600',
    bg: 'bg-red-100'
  },
  { 
    id: 'other', 
    label: 'Other', 
    icon: HelpCircle, 
    color: 'text-purple-600',
    bg: 'bg-purple-100'
  }
];

// Mock data for demonstration
const mockIssueReports: IssueReport[] = [
  {
    id: 'issue-1',
    user_id: 'user-1',
    category: 'waste_dump',
    description: 'Large pile of trash dumped by the riverside. Includes plastic waste and potentially hazardous materials.',
    location: {
      latitude: 37.7749,
      longitude: -122.4194,
      address: '123 River Road'
    },
    media_urls: [
      'https://images.pexels.com/photos/2768961/pexels-photo-2768961.jpeg?auto=compress&cs=tinysrgb&w=600'
    ],
    is_anonymous: false,
    status: 'verified',
    created_at: '2025-06-10T14:30:00Z',
    updated_at: '2025-06-10T16:45:00Z',
    points_awarded: 75,
    upvotes: 12,
    profiles: {
      username: 'EcoWarrior23',
      avatar_url: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
    }
  },
  {
    id: 'issue-2',
    user_id: 'user-2',
    category: 'water_contamination',
    description: 'Stream appears to be contaminated with chemical runoff. Water has an unusual color and odor.',
    location: {
      latitude: 37.7833,
      longitude: -122.4167,
      address: '456 Creek View'
    },
    media_urls: [
      'https://images.pexels.com/photos/2100942/pexels-photo-2100942.jpeg?auto=compress&cs=tinysrgb&w=600'
    ],
    is_anonymous: true,
    status: 'sent_to_authority',
    created_at: '2025-06-09T10:15:00Z',
    updated_at: '2025-06-10T09:30:00Z',
    points_awarded: 100,
    upvotes: 28,
    profiles: {
      username: 'Anonymous',
      avatar_url: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
    }
  },
  {
    id: 'issue-3',
    user_id: 'user-3',
    category: 'illegal_tree_cutting',
    description: 'Several trees being cut down in protected forest area without permits.',
    location: {
      latitude: 37.7850,
      longitude: -122.4300,
      address: '789 Forest Lane'
    },
    media_urls: [
      'https://images.pexels.com/photos/5748732/pexels-photo-5748732.jpeg?auto=compress&cs=tinysrgb&w=600'
    ],
    is_anonymous: false,
    status: 'resolved',
    created_at: '2025-06-05T08:45:00Z',
    updated_at: '2025-06-08T14:20:00Z',
    points_awarded: 150,
    upvotes: 45,
    profiles: {
      username: 'ForestGuardian',
      avatar_url: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
    }
  },
  {
    id: 'issue-4',
    user_id: 'user-4',
    category: 'construction_pollution',
    description: 'Construction site releasing dust and debris into the air and nearby stream.',
    location: {
      latitude: 37.7700,
      longitude: -122.4100,
      address: '101 Build Site'
    },
    media_urls: [
      'https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&w=600'
    ],
    is_anonymous: false,
    status: 'submitted',
    created_at: '2025-06-11T11:30:00Z',
    updated_at: '2025-06-11T11:30:00Z',
    points_awarded: 0,
    upvotes: 3,
    profiles: {
      username: 'CityWatcher',
      avatar_url: 'https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
    }
  }
];

export default function IssueReportList() {
  const [issueReports, setIssueReports] = useState<IssueReport[]>(mockIssueReports);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<IssueReport | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'verified' | 'sent_to_authority' | 'resolved' | 'rejected'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'my'>('all');
  const [error, setError] = useState<string | null>(null);
  const [isUpvoting, setIsUpvoting] = useState(false);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleUpvote = async (reportId: string) => {
    if (isUpvoting) return;
    
    setIsUpvoting(true);
    
    try {
      // Simulate upvote
      setIssueReports(prev => prev.map(report => 
        report.id === reportId 
          ? { ...report, upvotes: report.upvotes + 1 } 
          : report
      ));
      
      // Update selected report if it's the one being upvoted
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => prev ? { ...prev, upvotes: prev.upvotes + 1 } : null);
      }
    } catch (error) {
      console.error('Error upvoting report:', error);
      setError(`Failed to upvote: ${(error as Error).message}`);
    } finally {
      setIsUpvoting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'verified': return 'bg-blue-100 text-blue-800';
      case 'sent_to_authority': return 'bg-purple-100 text-purple-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'submitted': return 'Submitted';
      case 'verified': return 'Verified';
      case 'sent_to_authority': return 'Sent to Authority';
      case 'resolved': return 'Resolved';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const getCategoryIcon = (category: string) => {
    const categoryInfo = ISSUE_CATEGORIES.find(c => c.id === category);
    if (!categoryInfo) return HelpCircle;
    return categoryInfo.icon;
  };

  const getCategoryColor = (category: string) => {
    const categoryInfo = ISSUE_CATEGORIES.find(c => c.id === category);
    if (!categoryInfo) return { color: 'text-gray-600', bg: 'bg-gray-100' };
    return { color: categoryInfo.color, bg: categoryInfo.bg };
  };

  const getCategoryLabel = (category: string) => {
    const categoryInfo = ISSUE_CATEGORIES.find(c => c.id === category);
    return categoryInfo?.label || 'Unknown';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReports = issueReports.filter(report => {
    const matchesStatus = statusFilter === 'all' || report.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || report.category === categoryFilter;
    const matchesSearch = report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.location.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (report.profiles?.username && !report.is_anonymous && 
                          report.profiles.username.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading environmental issue reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Environmental Issues</h2>
          <p className="text-gray-600">
            View and track reported environmental issues in your community
          </p>
        </div>
        
        {/* View Mode Tabs */}
        <div className="inline-flex bg-gray-100 rounded-lg p-1 self-start">
          <button
            onClick={() => setViewMode('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'all'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All Reports
          </button>
          <button
            onClick={() => setViewMode('my')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'my'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            My Reports
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span className="font-medium text-red-800">Error</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by description or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="verified">Verified</option>
                <option value="sent_to_authority">Sent to Authority</option>
                <option value="resolved">Resolved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Categories</option>
                {ISSUE_CATEGORIES.map(category => (
                  <option key={category.id} value={category.id}>{category.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredReports.length} of {issueReports.length} reports
          </p>
          
          {(searchTerm || statusFilter !== 'all' || categoryFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setCategoryFilter('all');
              }}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Issue Reports List */}
      <div className="space-y-6">
        {filteredReports.map((report) => {
          const CategoryIcon = getCategoryIcon(report.category);
          const categoryStyle = getCategoryColor(report.category);
          
          return (
            <div 
              key={report.id} 
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedReport(report)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  {/* Category Icon */}
                  <div className={`p-3 rounded-lg ${categoryStyle.bg}`}>
                    <CategoryIcon className={`h-6 w-6 ${categoryStyle.color}`} />
                  </div>
                  
                  <div className="flex-1">
                    {/* Category and Status */}
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {getCategoryLabel(report.category)}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {getStatusLabel(report.status)}
                      </span>
                      {report.points_awarded > 0 && (
                        <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-medium">
                          +{report.points_awarded} points
                        </span>
                      )}
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-600 mb-3 line-clamp-2">{report.description}</p>
                    
                    {/* Location and Date */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-4 w-4" />
                        <span>{report.location.address}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{formatDate(report.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Media Preview */}
                {report.media_urls.length > 0 && (
                  <div className="hidden sm:block ml-4">
                    <div className="w-20 h-20 rounded-lg overflow-hidden">
                      {report.media_urls[0].includes('video') ? (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <Video className="h-8 w-8 text-gray-500" />
                        </div>
                      ) : (
                        <img
                          src={report.media_urls[0]}
                          alt="Issue evidence"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="flex items-center justify-between">
                {/* Reporter */}
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    {report.is_anonymous ? 'Anonymous' : report.profiles?.username || 'Unknown User'}
                  </span>
                </div>
                
                {/* Actions */}
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpvote(report.id);
                    }}
                    className="flex items-center space-x-1 text-gray-600 hover:text-emerald-600 transition-colors"
                  >
                    <ThumbsUp className="h-4 w-4" />
                    <span className="text-sm font-medium">{report.upvotes}</span>
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedReport(report);
                    }}
                    className="flex items-center space-x-1 text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    <span className="text-sm font-medium">View</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        
        {filteredReports.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
            <p className="text-gray-600">
              {viewMode === 'my' 
                ? "You haven't reported any environmental issues yet." 
                : "No environmental issues have been reported matching your filters."}
            </p>
          </div>
        )}
      </div>

      {/* Issue Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-emerald-600" />
                <h2 className="text-xl font-bold text-gray-900">Issue Details</h2>
              </div>
              <button
                onClick={() => setSelectedReport(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${
                selectedReport.status === 'resolved' 
                  ? 'bg-green-50 border border-green-200' 
                  : selectedReport.status === 'rejected'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center space-x-2">
                  {selectedReport.status === 'resolved' ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : selectedReport.status === 'rejected' ? (
                    <XCircle className="h-5 w-5 text-red-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-blue-600" />
                  )}
                  <span className={`font-medium ${
                    selectedReport.status === 'resolved' 
                      ? 'text-green-800' 
                      : selectedReport.status === 'rejected'
                      ? 'text-red-800'
                      : 'text-blue-800'
                  }`}>
                    Status: {getStatusLabel(selectedReport.status)}
                  </span>
                </div>
                
                <div className="mt-2 text-sm">
                  {selectedReport.status === 'resolved' ? (
                    <p className="text-green-700">This issue has been successfully resolved by local authorities.</p>
                  ) : selectedReport.status === 'rejected' ? (
                    <p className="text-red-700">This report was rejected. It may not have met our verification criteria.</p>
                  ) : selectedReport.status === 'verified' ? (
                    <p className="text-blue-700">This issue has been verified by our team and is awaiting action from authorities.</p>
                  ) : selectedReport.status === 'sent_to_authority' ? (
                    <p className="text-purple-700">This issue has been sent to local authorities for action.</p>
                  ) : (
                    <p className="text-yellow-700">This report is pending verification by our team.</p>
                  )}
                </div>
              </div>
              
              {/* Media Gallery */}
              {selectedReport.media_urls.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Evidence</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedReport.media_urls.map((url, index) => (
                      <div key={index} className="rounded-lg overflow-hidden border border-gray-200">
                        {url.includes('video') ? (
                          <div className="aspect-video bg-gray-100 flex items-center justify-center">
                            <Video className="h-12 w-12 text-gray-400" />
                          </div>
                        ) : (
                          <img
                            src={url}
                            alt={`Issue evidence ${index + 1}`}
                            className="w-full aspect-video object-cover"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Category and Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Issue Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  {/* Category */}
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${getCategoryColor(selectedReport.category).bg}`}>
                      {React.createElement(getCategoryIcon(selectedReport.category), {
                        className: `h-5 w-5 ${getCategoryColor(selectedReport.category).color}`
                      })}
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Category</span>
                      <p className="text-gray-700">{getCategoryLabel(selectedReport.category)}</p>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <span className="font-medium text-gray-900">Description</span>
                    <p className="text-gray-700 mt-1">{selectedReport.description}</p>
                  </div>
                  
                  {/* AI Analysis (if available) */}
                  {selectedReport.ai_confidence && selectedReport.ai_confidence > 0 && (
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <Eye className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-gray-900">AI Analysis</span>
                      </div>
                      <p className="text-sm text-gray-700">
                        Confidence: {Math.round(selectedReport.ai_confidence * 100)}%
                      </p>
                      {selectedReport.ai_tags && selectedReport.ai_tags.length > 0 && (
                        <div className="mt-1">
                          <p className="text-sm text-gray-700">Detected elements:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedReport.ai_tags.map((tag, index) => (
                              <span key={index} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Location */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Location</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="h-5 w-5 text-gray-600" />
                    <span className="font-medium text-gray-900">{selectedReport.location.address}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Coordinates: {selectedReport.location.latitude.toFixed(6)}, {selectedReport.location.longitude.toFixed(6)}
                  </p>
                  
                  {/* Map placeholder */}
                  <div className="mt-3 h-40 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-500">Map View</span>
                  </div>
                </div>
              </div>
              
              {/* Reporter and Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Reported By</h4>
                  <div className="flex items-center space-x-3">
                    {selectedReport.is_anonymous ? (
                      <>
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                        <span className="text-gray-700">Anonymous</span>
                      </>
                    ) : (
                      <>
                        <img
                          src={selectedReport.profiles?.avatar_url || ''}
                          alt={selectedReport.profiles?.username || 'User'}
                          className="w-10 h-10 rounded-full"
                        />
                        <span className="text-gray-700">{selectedReport.profiles?.username || 'Unknown User'}</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Timestamps</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reported:</span>
                      <span className="text-gray-900">{formatDate(selectedReport.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Updated:</span>
                      <span className="text-gray-900">{formatDate(selectedReport.updated_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => handleUpvote(selectedReport.id)}
                  disabled={isUpvoting}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                >
                  {isUpvoting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <ThumbsUp className="h-5 w-5" />
                  )}
                  <span>Upvote ({selectedReport.upvotes})</span>
                </button>
                
                <button
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                >
                  <Send className="h-5 w-5" />
                  <span>Share</span>
                </button>
                
                {selectedReport.status === 'sent_to_authority' && (
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <ExternalLink className="h-5 w-5" />
                    <span>Track with Authority</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}